<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog';
  import { Button } from '$lib/components/ui/button';
  import Input from '$lib/components/ui/input/input.svelte';
  import Label from '$lib/components/ui/label/label.svelte';
  import { Toaster, toast } from 'svelte-sonner';
  import { GUEST_RFQ_FIELDS, type FieldConfig } from './guestRfq.config';

  let {
    open = $bindable(false),
    cart = null as any,
    experience = null as any,
    fields = GUEST_RFQ_FIELDS as FieldConfig[],
    initialNotes = '' as string,
    quoteType = [] as string[],
  }: {
    open?: boolean;
    cart?: any;
    experience?: any;
    fields?: FieldConfig[];
    initialNotes?: string;
    quoteType?: string[];
  } = $props();

  let formData = $state<Record<string, string>>({});
  let notes = $state(initialNotes);
  let saveDesign = $state(false);
  let loading = $state(false);
  let errors = $state<Record<string, string>>({});
  let showConfirm = $state(false);

  let requiresDesignShare = $derived(quoteType?.includes('complete_unit') ?? false);

  $effect(() => {
    if (requiresDesignShare) {
      saveDesign = true;
    }
  });

  // Listen for guest RFQ completion
  $effect(() => {
    if (!experience?.eventHub) return;

    const handleComplete = (result: { success: boolean; data?: any }) => {
      loading = false;
      if (result.success) {
        toast.success('RFQ sent successfully!');
        setTimeout(() => { open = false; }, 1800);
      } else {
        toast.error('Failed to send RFQ. Please try again.');
      }
    };

    experience.eventHub.on('sendRFQ:guest:complete', handleComplete);
    return () => {
      experience.eventHub.off('sendRFQ:guest:complete', handleComplete);
    };
  });

  function validate(): boolean {
    const next: Record<string, string> = {};
    for (const field of fields) {
      const val = (formData[field.id] ?? '').trim();
      if (field.required && !val) {
        next[field.id] = `${field.label} is required`;
        continue;
      }
      if (field.type === 'email' && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        next[field.id] = 'Invalid email address';
      }
    }
    errors = next;
    return Object.keys(next).length === 0;
  }

  function onSendClick() {
    if (!validate()) return;
    if (saveDesign) {
      showConfirm = true;
    } else {
      submitRFQ();
    }
  }

  function submitRFQ() {
    showConfirm = false;
    loading = true;

    const user = {
      firstName:  formData.firstName ?? '',
      lastName:   formData.lastName  ?? '',
      first_name: formData.firstName ?? '',
      last_name:  formData.lastName  ?? '',
      email:   formData.email   ?? '',
      company: formData.company ?? '',
      phone:   formData.phone   ?? '',
      street:  formData.street  ?? '',
      city:    formData.city    ?? '',
      state:   formData.state   ?? '',
      zip:     formData.zip     ?? '',
    };

    const guestNotes = notes;

    let xmdData: string | null = null;
    if (saveDesign) {
      xmdData = sessionStorage.getItem('xmdVaultCache') ?? null;
    }
    console.log(cart, 'cart data')
    experience?.eventHub?.emit('sendRFQ:guest', {
      user,
      cart,
      notes: guestNotes,
      designId: cart?.designId ?? experience?.projectUuid ?? (typeof window !== 'undefined' ? window.currentProjectUuid : undefined),
      saveDesign,
      xmdData,
      quoteType,
    });
  }
</script>

<Toaster position="top-center" richColors />

<Dialog.Root bind:open preventScroll={false}>
  <Dialog.Content
    class="sm:max-w-[560px] border-none shadow-lg !z-[1000001] !bg-white dark:!bg-[var(--theme-main)] dark:!text-[var(--theme-font)]"
  >
    <Dialog.Header class="flex !flex-col !items-start !gap-2 !p-4 !pb-0 !mt-4">
      <Dialog.Title class="text-xl font-semibold">Send RFQ as Guest</Dialog.Title>
      <Dialog.Description class="text-sm text-muted-foreground !pl-1">
        Fill in your details below and we'll get back to you with a quote.
      </Dialog.Description>
    </Dialog.Header>

    <div class="flex !flex-col gap-4 !px-4 !py-4 overflow-y-auto max-h-[65vh]">
      <div class="grid !grid-cols-2 !gap-x-4 !gap-y-3 !px-4 !w-full">
        {#each fields as field (field.id)}
          <div
            class="flex !flex-col !items-start !justify-start gap-1 {field.span === 2 ? 'col-span-2' : 'col-span-1'}"
          >
            <Label for={field.id} class="text-left text-xs font-normal">
              {field.label}{field.required ? ' *' : ''}
            </Label>
            <Input
              id={field.id}
              type={field.type}
              placeholder={field.placeholder ?? ''}
              value={formData[field.id] ?? ''}
              oninput={(e: Event) => {
                formData[field.id] = (e.target as HTMLInputElement).value;
                if (errors[field.id]) {
                  const { [field.id]: _, ...rest } = errors;
                  errors = rest;
                }
              }}
              class="!w-full !px-4 !py-5 text-xs !outline-none
                    focus:!outline-none focus-visible:!outline-none
                    focus:ring-0 focus-visible:ring-0
                    !ring-0 !shadow-none focus:!shadow-none focus-visible:!shadow-none
                    {errors[field.id] ? 'border-red-400 focus-visible:border-red-400' : ''}"
            />
            {#if errors[field.id]}
              <p class="text-[11px] text-red-500 mt-0.5">{errors[field.id]}</p>
            {/if}
          </div>
        {/each}
      </div>

    </div>
    <div class="flex !items-start !gap-3 rounded-lg border border-slate-200 dark:border-[var(--theme-canvas)] bg-slate-50 dark:bg-[var(--theme-canvas)] !px-4 !py-3 mt-1">
      <input
        id="save-design-checkbox"
        type="checkbox"
        bind:checked={saveDesign}
        disabled={requiresDesignShare}
        class="mt-0.5 h-4 w-4 shrink-0 accent-[#004976] dark:accent-[var(--theme-link)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-70"
      />
      <div class="flex !flex-col !items-start !gap-0.5">
        <label for="save-design-checkbox" class="text-xs font-medium cursor-pointer select-none">
          Allow X-Microwave to save and review my design
        </label>
        <p class="text-[11px] text-muted-foreground leading-relaxed">
          Your current design will be saved for X-Microwave staff to review when processing your quote. You will not retain access to the saved design.
        </p>
      </div>
    </div>

    <!-- ? Maybe use this notes field -->
    <!-- <div class="w-full flex shrink-0 !flex-col !items-start gap-0 !px-4">
      <Label for="guest-notes" class="text-left text-xs font-normal">Notes</Label>
      <textarea
        id="guest-notes"
        bind:value={notes}
        placeholder="Leave your notes here..."
        rows="3"
        class="mt-1 w-full resize-none rounded-md border border-input bg-transparent !px-4 !py-2 text-xs
               !outline-none !ring-0 !shadow-none
               focus:!outline-none focus:!ring-0 focus:!shadow-none
               focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!shadow-none
               dark:text-[var(--theme-font)] dark:bg-[var(--theme-main)]"
      ></textarea>
    </div> -->

    <Dialog.Footer class="!p-4 !pt-0">
      <Button
        variant="outline"
        onclick={() => (open = false)}
        class="h-9 !px-4 !py-5 border-2 border-[#DADADA] !shadow-none !text-sm font-normal hover:brightness-110 transition"
      >
        Cancel
      </Button>
      <Button
        disabled={loading}
        onclick={onSendClick}
        class="h-9 !px-4 !py-5 border-none !shadow-none !text-sm font-normal hover:brightness-110 transition text-white !bg-[#004976] dark:!bg-[var(--theme-link)]"
      >
        {loading ? 'Sending…' : 'Send RFQ'}
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>

<Dialog.Root bind:open={showConfirm} preventScroll={false}>
  <Dialog.Content
    class="sm:max-w-[440px] border-none shadow-xl !z-[1000002] !bg-white dark:!bg-[var(--theme-main)] dark:!text-[var(--theme-font)]"
  >
    <Dialog.Header class="flex !flex-col !items-start !gap-2 !p-6 !pb-0">
      <Dialog.Title class="text-base font-semibold">Confirm data sharing</Dialog.Title>
      <Dialog.Description class="sr-only">
        Review what will be shared before sending your RFQ.
      </Dialog.Description>
    </Dialog.Header>

    <div class="!px-6 !py-4 flex !flex-col !gap-3">
      <p class="text-sm text-foreground leading-relaxed">
        By sending this RFQ you agree to share the following with X-Microwave:
      </p>
      <ul class="text-sm text-muted-foreground space-y-1.5 list-none">
        <li class="flex items-start gap-2">
          <span class="mt-0.5 text-[#004976] dark:text-[var(--theme-link)] shrink-0">›</span>
          <span>Your <strong class="text-foreground font-medium">current circuit design</strong>, stored securely for staff review</span>
        </li>
        <li class="flex items-start gap-2">
          <span class="mt-0.5 text-[#004976] dark:text-[var(--theme-link)] shrink-0">›</span>
          <span>Once submitted, <strong class="text-foreground font-medium">you will not have access</strong> to the saved design</span>
        </li>
      </ul>
    </div>

    <Dialog.Footer class="!px-6 !pb-5 !pt-0">
      <Button
        variant="outline"
        onclick={() => (showConfirm = false)}
        class="h-9 !px-4 !py-5 border-2 border-[#DADADA] !shadow-none !text-sm font-normal hover:brightness-110 transition"
      >
        Cancel
      </Button>
      <Button
        onclick={submitRFQ}
        class="h-9 !px-4 !py-5 border-none !shadow-none !text-sm font-normal hover:brightness-110 transition text-white !bg-[#004976] dark:!bg-[var(--theme-link)]"
      >
        Send RFQ
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>

<style>
  :global([data-sonner-toaster]) {
    z-index: 9999999 !important;
  }
</style>
